"use client";
import { getSubjectColorIndex } from "@/lib/strapi/formatters";
import { Box, Checkbox } from "@mantine/core";
import { SubTopic } from "@tsTypes/subject-schema";
import clsx from "clsx";
import { useMemo } from "react";
import {
  selectSubTopicSyllabusMetaById,
  subTopicSyllabusSelection,
} from "../../../../../../features/syllabus/subjectSyllabusChecklistSlice";
import {
  useCustomDispatch,
  useCustomSelector,
} from "../../../../../../lib/redux/hooks";
import SubTopicTitle from "../../components/titles/SubTopicTitle";
import PointInput from "./PointInput";
import classes from "./SyllabusRow.module.css";

export default function SyllabusRow({
  subjectNameLower,
  subjectDocumentId,
  topicDocumentId,
  subTopic,
  layout,
  isStriped,
  subTopicSyllabusPointDocumentIdMap,
  restrictAccess,
  isComplete,
}: {
  subjectNameLower: string;
  subjectDocumentId: string;
  topicDocumentId: string;
  subTopic: Pick<SubTopic, "documentId" | "RelatedSyllabusPoints">;
  layout: string[];
  isStriped: boolean;
  subTopicSyllabusPointDocumentIdMap: string[];
  restrictAccess: boolean;
  isComplete: boolean;
}) {
  const dispatch = useCustomDispatch();

  const subTopicPointMeta = useCustomSelector((state) =>
    restrictAccess
      ? undefined
      : selectSubTopicSyllabusMetaById(
          state,
          subjectDocumentId,
          topicDocumentId,
          subTopic.documentId
        )
  );

  const filteredSubTopicPoints = useMemo(() => {
    const result = {
      application: [] as JSX.Element[],
      understanding: [] as JSX.Element[],
    };

    subTopic.RelatedSyllabusPoints.forEach((syllabusPoint) => {
      const pointElement = (
        <PointInput
          key={syllabusPoint.documentId}
          subjectDocumentId={subjectDocumentId}
          topicDocumentId={topicDocumentId}
          subTopicDocumentId={subTopic.documentId}
          point={syllabusPoint}
          isChecked={
            subTopicPointMeta?.includes(syllabusPoint.documentId) ?? false
          }
          dispatch={dispatch}
          restrictAccess={restrictAccess}
          isComplete={isComplete}
        />
      );

      if (syllabusPoint.TypeOfPoint === "Application") {
        result.application.push(pointElement);
      } else {
        result.understanding.push(pointElement);
      }
    });

    return result;
  }, [
    subTopic,
    subTopicPointMeta,
    restrictAccess,
    isComplete,
    subjectDocumentId,
    topicDocumentId,
  ]);

  const isSubTopicComplete =
    subTopicPointMeta?.length === subTopicSyllabusPointDocumentIdMap?.length;

  return (
    <Box className={clsx(classes.row, isStriped && classes.striped)}>
      <Box
        className={classes.subtopicCell}
        style={{ flexBasis: layout[0] }}
      >
        <Checkbox
        variant="outline"
          color={isComplete ? "darkblue" : "lightblue"}
          checked={isSubTopicComplete}
          onChange={() => {
            if (restrictAccess) return;
            dispatch(
              subTopicSyllabusSelection({
                subjectDocumentId,
                topicDocumentId,
                subTopicDocumentId: subTopic.documentId,
                subTopicSyllabusPointDocumentIds:
                  subTopicSyllabusPointDocumentIdMap,
              })
            );
          }}
          size="sm"
          label={
            <SubTopicTitle
              subjectColorIndex={getSubjectColorIndex(subjectNameLower)}
              subTopicDocumentId={subTopic.documentId}
            />
          }
          styles={{
            root: { alignItems: "center" },
            body: { alignItems: "center" },
            labelWrapper: { flex: 1 },
            label: {
              wordBreak: "break-word",
              cursor: restrictAccess ? "default" : "pointer",
            },
            input: {
              cursor: restrictAccess ? "not-allowed" : "pointer",
              backgroundColor: restrictAccess ? "#ddd" : "transparent",
            },
          }}
        />
      </Box>

      <Box className={classes.pointsCell} style={{ flexBasis: layout[1] }}>
        {filteredSubTopicPoints.understanding}
      </Box>
    </Box>
  );
}
